import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
interface ProjectData {
  id: string;
  title: string;
  subtitle: string;
  client: string;
  industry: string;
  role: string;
  service: string;
  challenge: string;
  blueprint: string[];
  results: {
    stats: string[];
    summary: string;
  };
  images: string[];
  nextProjects: {
    id: string;
    title: string;
  }[];
}
const projects: Record<string, ProjectData> = {
  'plus-accountants': {
    id: 'plus-accountants',
    title: 'From Dormant to Dominant',
    subtitle: "Reigniting Plus Accountants' Growth Engine",
    client: 'Plus Accountants',
    industry: 'Financial Services',
    role: 'Digital Growth Architect',
    service: 'Full Ecosystem Build',
    challenge: 'Plus Accountants, an established Australian firm, had hit a revenue plateau. Their large client base was completely disengaged, they had zero social media presence, and no predictable system for generating new business, leaving them vulnerable to modern competitors.',
    blueprint: ['01. Immediate Revenue Activation: I began by launching a targeted re-engagement campaign to their existing client list via Zoho CRM and an optimized WhatsApp channel, securing over 50 clients in the first two months.', '02. Foundational Brand Architecture: We then refreshed their brand assets and defined a clear brand message to project a more modern, authoritative image across all platforms.', '03. Organic Ecosystem Construction: I built and optimized their presence on key social platforms, deploying platform-specific content strategies that established them as thought leaders without any ad spend.', '04. Systemization & Scaling: I implemented a custom CRM and sales tracking process to manage the new influx of leads, creating a sustainable growth system that their internal staff continues to use today.'],
    results: {
      stats: ['2x Monthly Revenue Growth', '70+ New Client Contracts Closed', '20+ High-Value Upsells', '347 New Prospects Engaged'],
      summary: 'The final outcome was a complete business transformation. We not only doubled their monthly revenue but also built a self-sustaining digital ecosystem that provides them with a predictable flow of new clients and a clear, organized system to manage their growth.'
    },
    images: ['https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1742&q=80', 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1115&q=80'],
    nextProjects: [{
      id: 'dua-construction',
      title: 'From Invisible to In-Demand: A Digital Turnaround for Dua Construction Services'
    }, {
      id: 'abh-systems',
      title: 'The Lean Launch: Generating 35+ Sales on a Shoestring Budget'
    }]
  },
  'dua-construction': {
    id: 'dua-construction',
    title: 'From Invisible to In-Demand',
    subtitle: 'A Digital Turnaround for a High-Value Construction Business',
    client: 'Dua Construction Services',
    industry: 'Construction & Real Estate',
    role: 'Digital Turnaround Lead',
    service: 'Digital Infrastructure & Lead Generation',
    challenge: 'Dua Construction Services was digitally non-existent. Their social media was a chaotic mix of inconsistent, unbranded posts, they had no professional website, and a banned Google My Business profile had rendered them invisible to local clients, effectively cutting off their primary source of high-value project leads.',
    blueprint: ['01. Immediate Triage & Reputation Recovery: My first move was a strategic circumvention of their Google ban. By establishing and verifying a dominant business presence on Yandex, we instantly restored their local search viability and opened up a new lead channel.', '02. Building the Pillars of Credibility: I architected their foundational digital assets from scratch. This included building their first professional, multi-page WordPress website and creating an optimized LinkedIn profile for the owner to establish him as an industry authority.', '03. Content Transformation & Optimization: I conducted a full audit of their existing project photos and data. I then re-engineered their boring, generic posts into a strategic library of high-converting social media content, complete with expert-level copy and researched hashtags.', '04. Lead Generation & Systemization: I implemented a consistent content deployment schedule and optimized their WhatsApp Business account to professionally manage all incoming inquiries, ensuring no lead was ever missed.'],
    results: {
      stats: ['2 Major Contracts Secured', '20+ Qualified Project Leads Generated', '10-Marla House in Bahria Town', '5-Marla Commercial Building'],
      summary: 'By building a credible and professional digital presence from the ground up, we transformed their business from a digital liability into a powerful lead-generation asset. The system I architected directly resulted in securing two monumental, high-value construction contracts—a testament to the power of a well-executed digital ecosystem.'
    },
    images: ['https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1931&q=80', 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', 'https://images.unsplash.com/photo-1626785774573-4b799315345d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1171&q=80'],
    nextProjects: [{
      id: 'plus-accountants',
      title: "From Dormant to Dominant: Reigniting an Accounting Firm's Growth Engine"
    }, {
      id: 'abh-systems',
      title: 'The Lean Launch: Generating 35+ Sales on a Shoestring Budget'
    }]
  },
  'abh-systems': {
    id: 'abh-systems',
    title: 'The Lean Launch',
    subtitle: 'Architecting a Profitable Tech Brand From Zero',
    client: 'ABH Systems (Laptops Division)',
    industry: 'E-commerce & IT Equipment',
    role: 'Go-to-Market Strategist',
    service: 'Full Ecosystem Architecture & Launch',
    challenge: 'ABH Systems was launching a new laptop subsidiary into a hyper-competitive market with absolutely nothing—no brand, no online presence, no marketing strategy, and zero customers. The critical challenge was to build an entire business from the ground up and achieve immediate profitability on a minimal, shoestring budget.',
    blueprint: ['01. Forensic Market Research: I began with an exhaustive market intelligence phase, analyzing everything from Reddit communities and Google Trends to competitor ad libraries to define a precise, in-demand niche and target audience.', '02. Complete Brand Architecture: Using the research data, I engineered the entire brand identity from scratch. This included developing their Unique Selling Proposition (USP), logos, color palettes, tone of voice, and all foundational marketing collateral.', '03. Omnichannel Sales Infrastructure: I architected and built their commercial presence across more than ten platforms, including social channels for brand building (Facebook, Instagram, TikTok) and high-intent marketplaces for immediate sales (OLX, Daraz, Facebook Marketplace).', '04. Hyper-Efficient Go-to-Market Execution: I managed the entire launch process, from product photoshoots and pricing strategy to deploying a powerful organic content machine. This was supplemented by surgical, micro-spend ad campaigns on marketplaces to ignite sales with maximum capital efficiency.'],
    results: {
      stats: ['35+ Sales in First 60 Days', 'Exceptional ROI on 2,800 PKR Ad Spend', '10+ Platforms Launched', 'From 0 to Profitable in Under 3 Months'],
      summary: 'This project is a testament to the power of a meticulously architected launch. By focusing on deep research and capital efficiency, we successfully took a concept and turned it into a profitable, revenue-generating business in under three months, proving the business model with an almost unbelievable return on investment.'
    },
    images: ['https://images.unsplash.com/photo-1661961110372-8a7682543120?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80', 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80', 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80'],
    nextProjects: [{
      id: 'plus-accountants',
      title: "From Dormant to Dominant: Reigniting an Accounting Firm's Growth Engine"
    }, {
      id: 'dua-construction',
      title: 'From Invisible to In-Demand: A Digital Turnaround for Dua Construction Services'
    }]
  }
};
const ProjectDetail: React.FC = () => {
  const {
    projectId
  } = useParams<{
    projectId: string;
  }>();
  const project = projects[projectId || ''];
  if (!project) {
    return <div className="min-h-screen pt-32 pb-20 container mx-auto px-4 flex items-center justify-center" data-id="element-325">
        <div className="text-center" data-id="element-326">
          <h1 className="text-3xl font-bold mb-4" data-id="element-327">Project Not Found</h1>
          <Link to="/" className="text-[#39FF14] hover:underline" data-id="element-328">
            Return to Home
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen pt-32 pb-20" data-id="element-329">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12" data-id="element-330">
        <div className="max-w-4xl mx-auto" data-id="element-331">
          <h1 className="text-4xl md:text-6xl font-bold mb-4" data-id="element-332">
            {project.title}
          </h1>
          <p className="text-xl font-thin mb-12" data-id="element-333">{project.subtitle}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-id="element-334">
            <div data-id="element-335">
              <p className="font-thin text-sm" data-id="element-336">Client</p>
              <p className="font-normal" data-id="element-337">{project.client}</p>
            </div>
            <div data-id="element-338">
              <p className="font-thin text-sm" data-id="element-339">Industry</p>
              <p className="font-normal" data-id="element-340">{project.industry}</p>
            </div>
            <div data-id="element-341">
              <p className="font-thin text-sm" data-id="element-342">My Role</p>
              <p className="font-normal" data-id="element-343">{project.role}</p>
            </div>
            <div data-id="element-344">
              <p className="font-thin text-sm" data-id="element-345">Key Service</p>
              <p className="font-normal" data-id="element-346">{project.service}</p>
            </div>
          </div>
        </div>
      </section>
      {/* The Challenge */}
      <section className="container mx-auto px-4 py-12" data-id="element-347">
        <div className="max-w-4xl mx-auto" data-id="element-348">
          <h2 className="text-2xl md:text-4xl font-bold mb-6" data-id="element-349">The Challenge</h2>
          <p className="font-thin text-lg" data-id="element-350">{project.challenge}</p>
        </div>
      </section>
      {/* My Architectural Blueprint */}
      <section className="container mx-auto px-4 py-12" data-id="element-351">
        <div className="max-w-4xl mx-auto" data-id="element-352">
          <h2 className="text-2xl md:text-4xl font-bold mb-6" data-id="element-353">
            My Architectural Blueprint
          </h2>
          <div className="space-y-6" data-id="element-354">
            {project.blueprint.map((step, index) => <p key={index} className="font-thin text-lg" data-id="element-355">
                {step}
              </p>)}
          </div>
        </div>
      </section>
      {/* Project Visuals */}
      <section className="container mx-auto px-4 py-12" data-id="element-356">
        <div className="max-w-4xl mx-auto" data-id="element-357">
          <h2 className="text-2xl md:text-4xl font-bold mb-6" data-id="element-358">
            Project Visuals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-359">
            {project.images.map((image, index) => <div key={index} className="rounded-xl overflow-hidden h-[300px]" data-id="element-360">
                <img src={image} alt={`Project visual ${index + 1}`} className="w-full h-full object-cover" data-id="element-361" />
              </div>)}
          </div>
        </div>
      </section>
      {/* The Results */}
      <section className="container mx-auto px-4 py-12" data-id="element-362">
        <div className="max-w-4xl mx-auto" data-id="element-363">
          <h2 className="text-2xl md:text-4xl font-bold mb-6" data-id="element-364">The Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-365">
            <div className="space-y-4" data-id="element-366">
              {project.results.stats.map((stat, index) => <p key={index} className="text-xl md:text-2xl font-bold" data-id="element-367">
                  {stat}
                </p>)}
            </div>
            <div data-id="element-368">
              <p className="font-thin text-lg" data-id="element-369">{project.results.summary}</p>
            </div>
          </div>
        </div>
      </section>
      {/* Next Project Navigation */}
      <section className="container mx-auto px-4 py-12" data-id="element-370">
        <div className="max-w-4xl mx-auto" data-id="element-371">
          <h2 className="text-2xl font-bold mb-6" data-id="element-372">Explore More Projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-373">
            {project.nextProjects.map(nextProject => <Link key={nextProject.id} to={`/projects/${nextProject.id}`} className="group block p-6 bg-white dark:bg-[#121212] rounded-xl hover:shadow-lg transition-all" data-id="element-374">
                <h3 className="text-lg font-bold mb-2 group-hover:text-[#39FF14] transition-colors" data-id="element-375">
                  {nextProject.title}
                </h3>
                <div className="flex items-center text-[#39FF14]" data-id="element-376">
                  <span className="mr-2" data-id="element-377">View Project</span>
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-378" />
                </div>
              </Link>)}
          </div>
        </div>
      </section>
    </div>;
};
export default ProjectDetail;